import { useState } from 'react';

import { Piece, PieceAction } from '@apiContract';

import { apiRequest } from '@src/api/apiRequest';
import { uploadImageToS3 } from '@src/api/imageS3';
import { getUploadParamsForImage } from '@components/MailpieceActions/components/actions/FinishDeliveryWithImage/helper';
import { useFinishPieceMutation } from '@components/MailpieceActions/hooks/useFinishPieceMutation';
import { ValidationData } from '@components/MailpieceActions/types';
import { resizeImage } from '@utils/image';

const actionTypes = {
  delivery: PieceAction.FinishDeliveryWithImage,
  pickup: PieceAction.FinishPickupWithImage,
}

const keyTypes = {
  delivery: 'deliveryImageKey',
  pickup: 'pickupImageKey',
}

export const useUploadImage = (
  mailpieces: Piece[],
  onSuccess: () => void,
  type?: string,
  validationData?: ValidationData,
) => {
  const { runAction, isLoading } = useFinishPieceMutation();
  const [isUploading, setIsUploading] = useState(false);

  const uploadImage = async (imageSrc: string, skipNotifyRecipient: boolean, comment: string, notifyMailroom: boolean) => {
    try {
      setIsUploading(true);
      const { url, key } = await getUploadParamsForImage(mailpieces[0].id);
      const resizedImage = await resizeImage(imageSrc, 4096, 4096);

      await apiRequest(
        uploadImageToS3,
        {
          image: resizedImage,
          uploadUrl: url,
          contentType: 'image/jpg',
        },
        false,
        5,
      );

      await runAction({
        action: actionTypes[type as keyof typeof actionTypes],
        pieces: mailpieces,
        comment,
        notifyMailroom,
        skipNotifyRecipient,
        validationData,
        additionalData: { [keyTypes[type as keyof typeof keyTypes]]: key },
      });

      onSuccess();
    } catch (error) {
      console.error('Failed to process image upload:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return { uploadImage, loading: isUploading || isLoading };
};
